/**
 * What a dependency asserts about the pair. Both kinds imply the same ordering
 * ("from" before "to") and so share cycle- and violation-detection; they differ in
 * what the author means by the link, and therefore in how it reads in the UI.
 *
 * - `order` — a sequencing constraint: this has to be read before that.
 * - `seed`  — a content thread: the earlier section plants something the later one
 *             pays off (a reference, an echo, a Chekhov seed).
 */
export type DependencyKind = 'order' | 'seed'

/**
 * Soft dependencies between ChapterSections.
 * A dependency states: `fromKey` must come BEFORE `toKey` in the reader-facing order.
 * Soft means: violations show a warning, but ordering and saving are not blocked.
 *
 * Endpoints are `filename:blockId` ChapterSection keys, so a dependency is independent
 * of whether the book is rendered as chapters or as a flat sequence.
 */
export interface ChapterSectionDependency {
  id: string
  fromKey: string
  toKey: string
  kind: DependencyKind
  reason: string
  createdAt: string
}

export function generateDependencyId(): string {
  const hex = () => Math.random().toString(16).slice(2, 10)
  return `${hex()}${hex()}`
}

/**
 * Return the subset of `deps` whose ordering is violated by `orderedKeys`.
 * A dep is violated when both endpoints are present in `orderedKeys` AND
 * `indexOf(toKey) < indexOf(fromKey)`. Deps with a missing endpoint are ignored.
 */
export function findViolations(deps: ChapterSectionDependency[], orderedKeys: string[]): ChapterSectionDependency[] {
  const index = new Map<string, number>()
  for (let i = 0; i < orderedKeys.length; i++) {
    index.set(orderedKeys[i], i)
  }
  const out: ChapterSectionDependency[] = []
  for (const dep of deps) {
    const fromIdx = index.get(dep.fromKey)
    const toIdx = index.get(dep.toKey)
    if (fromIdx === undefined || toIdx === undefined) {
      continue
    }
    if (toIdx < fromIdx) {
      out.push(dep)
    }
  }
  return out
}

/**
 * Return deps where BOTH endpoints exist in the canonical key set.
 * Use only for transient UI segregation; do NOT persist this filter automatically,
 * so deps survive cut-paste grace windows like chapterSection refs do.
 */
export function partitionDepsByAliveness(
  deps: ChapterSectionDependency[],
  canonicalKeys: Set<string>,
): { alive: ChapterSectionDependency[]; orphaned: ChapterSectionDependency[] } {
  const alive: ChapterSectionDependency[] = []
  const orphaned: ChapterSectionDependency[] = []
  for (const dep of deps) {
    if (canonicalKeys.has(dep.fromKey) && canonicalKeys.has(dep.toKey)) {
      alive.push(dep)
    } else {
      orphaned.push(dep)
    }
  }
  return { alive, orphaned }
}

/** Find an existing edge `(fromKey, toKey)` regardless of reason. */
export function findDependency(
  deps: ChapterSectionDependency[],
  fromKey: string,
  toKey: string,
): ChapterSectionDependency | undefined {
  return deps.find((d) => d.fromKey === fromKey && d.toKey === toKey)
}

/**
 * Would adding `fromKey → toKey` create a directed cycle in the dep graph?
 * Returns true iff there's already a path from `toKey` back to `fromKey`
 * (or fromKey === toKey, a degenerate self-loop).
 */
export function wouldCreateCycle(deps: ChapterSectionDependency[], fromKey: string, toKey: string): boolean {
  return findCyclePath(deps, fromKey, toKey) !== null
}

/**
 * If adding `fromKey → toKey` would close a directed cycle, return the full
 * cycle path: `[fromKey, toKey, ..., fromKey]`. Otherwise return null.
 *
 * The first hop (fromKey → toKey) is the proposed new edge; subsequent hops
 * are existing edges. Uses BFS so the returned cycle is shortest by hop count.
 * The candidate edge itself is skipped during traversal so editing an
 * existing dep's reason doesn't spuriously flag a cycle.
 */
export function findCyclePath(deps: ChapterSectionDependency[], fromKey: string, toKey: string): string[] | null {
  if (fromKey === toKey) {
    return [fromKey, fromKey]
  }
  const adj = new Map<string, string[]>()
  for (const d of deps) {
    if (d.fromKey === fromKey && d.toKey === toKey) {
      continue
    }
    const list = adj.get(d.fromKey) ?? []
    list.push(d.toKey)
    adj.set(d.fromKey, list)
  }
  // BFS from toKey looking for fromKey; remember predecessors to reconstruct the path.
  const prev = new Map<string, string | null>()
  prev.set(toKey, null)
  const queue: string[] = [toKey]
  while (queue.length > 0) {
    const cur = queue.shift() as string
    if (cur === fromKey) {
      const existingPath: string[] = []
      let node: string | null = cur
      while (node !== null) {
        existingPath.unshift(node)
        node = prev.get(node) ?? null
      }
      // Prepend fromKey to close the cycle: [from, to, ..., from].
      return [fromKey, ...existingPath]
    }
    for (const next of adj.get(cur) ?? []) {
      if (!prev.has(next)) {
        prev.set(next, cur)
        queue.push(next)
      }
    }
  }
  return null
}

/**
 * Would `fromKey → toKey` be violated by the given linear order?
 * Returns true iff both endpoints exist in `orderedKeys` and toKey appears before fromKey.
 */
export function wouldViolateOrder(fromKey: string, toKey: string, orderedKeys: string[]): boolean {
  let fromIdx = -1
  let toIdx = -1
  for (let i = 0; i < orderedKeys.length; i++) {
    if (orderedKeys[i] === fromKey) {
      fromIdx = i
    }
    if (orderedKeys[i] === toKey) {
      toIdx = i
    }
  }
  if (fromIdx === -1 || toIdx === -1) {
    return false
  }
  return toIdx < fromIdx
}

/** Count incoming / outgoing edges per key. */
export function buildDependencyDegreeMap(
  deps: ChapterSectionDependency[],
): Map<string, { incoming: number; outgoing: number }> {
  const out = new Map<string, { incoming: number; outgoing: number }>()
  const bump = (key: string, field: 'incoming' | 'outgoing') => {
    const existing = out.get(key) ?? { incoming: 0, outgoing: 0 }
    existing[field]++
    out.set(key, existing)
  }
  for (const d of deps) {
    bump(d.fromKey, 'outgoing')
    bump(d.toKey, 'incoming')
  }
  return out
}
